import AssignmentTurnedInRounded from '@mui/icons-material/AssignmentTurnedInRounded';
import { Outlet, type RouteObject } from 'react-router';
import { RequireRole } from '@/platform/guards';
import type { FrontModule } from '@/modules/types';
import { HomeStaff, HomeStudent } from './components/HomeWidgets';

const STAFF = ['admin', 'coordinator', 'teacher'] as const;
// Only the coordination and the admins register, edit, release and grade.
const MANAGERS = ['admin', 'coordinator'] as const;

const page = (
  loader: () => Promise<Record<string, React.ComponentType>>,
  name: string,
): Pick<RouteObject, 'lazy'> => ({
  lazy: async () => ({ Component: (await loader())[name] }),
});

/** Assessments: question bank, quizzes/simulados, grading and reports. Back-end: back/app/modules/assessments. */
export const assessmentsModule: FrontModule = {
  key: 'assessments',
  icon: AssignmentTurnedInRounded,
  nav: [
    {
      to: '/assessments',
      label: 'Avaliações',
      icon: AssignmentTurnedInRounded,
      roles: ['admin', 'coordinator', 'teacher', 'student'],
      // Students on their own (ENEM) belong to no institution: nobody releases quizzes to them.
      hiddenFor: (user) => Boolean(user.independent),
    },
  ],
  routes: [
    { path: '/assessments', ...page(() => import('./pages/QuizzesPage'), 'QuizzesPage') },
    {
      path: '/assessments/my/:quizId',
      ...page(() => import('./pages/QuizIntroPage'), 'QuizIntroPage'),
    },
    {
      path: '/assessments/attempts/:attemptId/result',
      ...page(() => import('./pages/ResultPage'), 'ResultPage'),
    },
    // Question review: for whoever was invited (any role; the API checks the profile).
    { path: '/revisao', ...page(() => import('./pages/ReviewPage'), 'ReviewPage') },
    {
      path: '/revisao/questoes/:questionId',
      ...page(() => import('./pages/ReviewQuestionPage'), 'ReviewQuestionPage'),
    },
    {
      element: (
        <RequireRole roles={['admin']}>
          <Outlet />
        </RequireRole>
      ),
      children: [
        {
          path: '/admin/revisores',
          ...page(() => import('./pages/ReviewersPage'), 'ReviewersPage'),
        },
      ],
    },
    {
      element: (
        <RequireRole roles={[...MANAGERS]}>
          <Outlet />
        </RequireRole>
      ),
      children: [
        {
          path: '/assessments/quizzes/:quizId',
          ...page(() => import('./pages/QuizEditorPage'), 'QuizEditorPage'),
        },
        {
          path: '/assessments/questions',
          ...page(() => import('./pages/QuestionBankPage'), 'QuestionBankPage'),
        },
        {
          path: '/assessments/categories',
          ...page(() => import('./pages/CategoriesPage'), 'CategoriesPage'),
        },
        {
          path: '/assessments/grading',
          ...page(() => import('./pages/GradingPage'), 'GradingPage'),
        },
      ],
    },
    {
      element: (
        <RequireRole roles={[...STAFF]}>
          <Outlet />
        </RequireRole>
      ),
      children: [
        {
          path: '/assessments/quizzes/:quizId/report',
          ...page(() => import('./pages/QuizReportPage'), 'QuizReportPage'),
        },
        {
          path: '/assessments/reports',
          ...page(() => import('./pages/ReportsPage'), 'ReportsPage'),
        },
        {
          path: '/assessments/students/:userId',
          ...page(() => import('./pages/StudentReportPage'), 'StudentReportPage'),
        },
        {
          path: '/assessments/attempts/:attemptId/review',
          ...page(() => import('./pages/AttemptReviewPage'), 'AttemptReviewPage'),
        },
      ],
    },
  ],
  fullscreenRoutes: [
    { path: '/exam/:attemptId', ...page(() => import('./pages/TakeQuizPage'), 'TakeQuizPage') },
    {
      element: (
        <RequireRole roles={[...MANAGERS]}>
          <Outlet />
        </RequireRole>
      ),
      children: [
        {
          path: '/assessments/quizzes/:quizId/preview',
          ...page(() => import('./pages/TakeQuizPage'), 'PreviewQuizPage'),
        },
      ],
    },
  ],
  HomeStaff,
  HomeStudent,
};
